import { EMPTY_VALUE, formatQuantity } from './jobDetailFormat'

// Historia 160, enmienda E-11 (RN58-RN67; 03-dominio §"Enmienda E-11" §9) — la pestaña Inspecciones pinta
// cada característica POR SU TIPO (`dataType` de D3). Pieza pura, sin React: `t_` es la traducción del
// componente y `language` el idioma para los decimales (RN30).

export const FEATURE_DATA_TYPE = {
  RANGE: 'RANGE',
  BOOLEAN: 'BOOLEAN',
  LIST: 'LIST',
  TEXT: 'TEXT',
  DATE: 'DATE',
  NO_RESPONSE: 'NO_RESPONSE'
}

/** Qué pinta la columna RESULTADO (RN65). */
export const INSPECTION_RESULT = {
  RANGE: 'RANGE',
  BOOLEAN: 'BOOLEAN',
  NO_VERDICT: 'NO_VERDICT',
  REPORTED: 'REPORTED',
  NONE: 'NONE'
}

// §9/§10: `Free text` y `Measure` evitan los literales en conflicto de `Text` y `Measurement`.
const TYPE_LABEL_KEY = {
  RANGE: 'Measure',
  BOOLEAN: 'Yes/No',
  LIST: 'List',
  TEXT: 'Free text',
  DATE: 'Date',
  NO_RESPONSE: 'Informative'
}

const RESULT_KIND = {
  RANGE: INSPECTION_RESULT.RANGE,
  BOOLEAN: INSPECTION_RESULT.BOOLEAN,
  LIST: INSPECTION_RESULT.NO_VERDICT,
  TEXT: INSPECTION_RESULT.REPORTED,
  DATE: INSPECTION_RESULT.REPORTED,
  NO_RESPONSE: INSPECTION_RESULT.NONE
}

// El signo menos tipográfico de la especificación de §9 ("−0,1").
const MINUS = '−'

// Los campos de D3 son nulables por contrato: un 0 es un valor, solo null (o ausente) no lo es.
const isPresent = (value) => value !== null && value !== undefined

export const typeLabelKey = (dataType = '') => TYPE_LABEL_KEY[dataType] || TYPE_LABEL_KEY.TEXT

export const isReportable = (line = {}) => line.dataType !== FEATURE_DATA_TYPE.NO_RESPONSE

export const resultKind = (line = {}) => RESULT_KIND[line.dataType] || INSPECTION_RESULT.REPORTED

/** "+0,04" / "−0,1" / "0" con el separador del idioma (desviación del último valor, RN61). */
export const signedQuantity = (value = 0, language = 'en') => {
  const number = Number(value) || 0
  const magnitude = formatQuantity(Math.abs(number), language)
  if (number > 0) return '+' + magnitude
  if (number < 0) return MINUS + magnitude
  return magnitude
}

/** RN64: la especificación según el tipo. Nominal y tolerancias solo se pintan en RANGE. */
export const specificationText = (line = {}, t_ = (key) => key, language = 'en') => {
  switch (line.dataType) {
    case FEATURE_DATA_TYPE.RANGE: {
      if (!isPresent(line.nominal)) return EMPTY_VALUE
      const tolerances = [
        isPresent(line.upperTolerance) ? signedTolerance(line.upperTolerance, language) : '',
        isPresent(line.lowerTolerance) ? MINUS + formatQuantity(Math.abs(Number(line.lowerTolerance) || 0), language) : ''
      ].filter((part) => part !== '').join(' / ')
      return [formatQuantity(line.nominal, language), tolerances, line.unit || ''].filter((part) => part !== '').join(' ')
    }
    case FEATURE_DATA_TYPE.BOOLEAN:
      return t_('OK expected')
    case FEATURE_DATA_TYPE.LIST:
      return (line.options || []).length > 0 ? line.options.join(' · ') : EMPTY_VALUE
    case FEATURE_DATA_TYPE.NO_RESPONSE:
      return t_('No response required')
    default:
      return EMPTY_VALUE
  }
}

// La tolerancia superior lleva su signo: "+" si es ≥ 0 (lo normal) y "−" si es negativa.
const signedTolerance = (value, language) => {
  const number = Number(value) || 0
  return (number < 0 ? MINUS : '+') + formatQuantity(Math.abs(number), language)
}

/**
 * H21/B-J11 (2026-09-27, hub e049d60): la especificación EN LA HUELLA (D6b, una sola pieza) es MÁS
 * POBRE que la de la pestaña (`specificationText`, que esta función nunca toca ni reimplementa): TEXT/
 * DATE ya se dejaban vacíos; LIST y RANGE sin `nominal` TAMBIÉN quedan vacíos, sin ningún "—". C4 no
 * publica `options` (R-HANDOFF-1): la huella no los lee NUNCA, ni siquiera para decidir si pintar el
 * "—" que sí usa la pestaña con una LIST sin opciones. El resto (RANGE con nominal, BOOLEAN,
 * NO_RESPONSE) delega en `specificationText` sin cambio.
 */
export const footprintSpecificationText = (line = {}, t_ = (key) => key, language = 'en') => {
  if (line.dataType === FEATURE_DATA_TYPE.TEXT || line.dataType === FEATURE_DATA_TYPE.DATE || line.dataType === FEATURE_DATA_TYPE.LIST) return ''
  if (line.dataType === FEATURE_DATA_TYPE.RANGE && !isPresent(line.nominal)) return ''
  return specificationText(line, t_, language)
}

/** RN61/§9: la mini barra solo con RANGE, último valor, nominal y AMBAS tolerancias (nulos, no falsy). */
export const deviationBarShown = (line = {}) =>
  line.dataType === FEATURE_DATA_TYPE.RANGE &&
  isPresent(line.lastValue) &&
  isPresent(line.nominal) &&
  isPresent(line.lowerTolerance) &&
  isPresent(line.upperTolerance)

const NO_GROUP_KEY = '__no-group__'
const orderOf = (line) => (isPresent(line.order) ? line.order : Number.POSITIVE_INFINITY)

/**
 * UX-9/O-J5: el esqueleto de agrupar+ordenar por `group` (RN58/RN67 + "E-11 precisiones" (1)) es el
 * MISMO para la pestaña Inspecciones del job (`groupInspections`, cobertura agregada) y para la fila
 * INSPECCIÓN de la huella (`groupFootprintInspections`, historia 169, cobertura de ESTA pieza) — solo
 * cambia qué campos extra lleva cada sección, nunca el agrupado/orden. `aggregate(groupLines)` decide
 * esos campos extra; esta función nunca los inventa. Una sección por grupo, ordenadas por el menor
 * `order` de sus características (nulos al final) y, a igualdad, por nombre; la de sin grupo siempre la
 * última. Dentro de cada sección las líneas van en el orden en que llegan (el que fija Sun, S203).
 */
export const groupFeaturesByGroup = (lines = [], aggregate = () => ({})) => {
  const byKey = new Map()
  lines.forEach((line) => {
    const key = line.group ? 'group-' + line.group.id : NO_GROUP_KEY
    if (!byKey.has(key)) {
      byKey.set(key, { key, name: line.group ? line.group.name || '' : '', noGroup: !line.group, lines: [], minOrder: Number.POSITIVE_INFINITY })
    }
    const section = byKey.get(key)
    section.lines.push(line)
    section.minOrder = Math.min(section.minOrder, orderOf(line))
  })
  return [...byKey.values()]
    .sort((left, right) => {
      if (left.noGroup !== right.noGroup) return left.noGroup ? 1 : -1
      if (left.minOrder !== right.minOrder) return left.minOrder < right.minOrder ? -1 : 1
      return left.name.localeCompare(right.name)
    })
    .map(({ minOrder, ...section }) => ({ ...section, ...aggregate(section.lines) }))
}

/**
 * RN58/RN67 + "E-11 precisiones" (1): una sección por grupo (ver `groupFeaturesByGroup`). Chip "c / t":
 * t = reportables del grupo, c = reportables con ≥ 1 pieza reportada (D33).
 * @returns {Array<{ key: string, name: string, noGroup: boolean, lines: object[], reported: number, reportable: number }>}
 */
export const groupInspections = (lines = []) => groupFeaturesByGroup(lines, (groupLines) => {
  let reported = 0
  let reportable = 0
  groupLines.forEach((line) => {
    if (isReportable(line)) {
      reportable += 1
      if ((line.reportedParts || 0) > 0) reported += 1
    }
  })
  return { reported, reportable }
})

/**
 * UX-9 (historia 169; precisión (3) del orquestador tras leer el literal definitivo de S79): cobertura
 * de un grupo para ESTA PIEZA (no agregada como en la pestaña del job). Cuenta SOLO las reportables SIN
 * MEDIR que NO sean por muestreo (D4/H9: una `Not measured due to sampling` no degrada la cobertura,
 * solo la genuina `Not measured`). Un grupo SIN NINGUNA reportable (solo informativas, NO_RESPONSE) NO
 * LLEVA CHIP en absoluto — `coverageKey` sale `null` y el componente omite el `Pill` entero.
 */
export const groupCoverage = (lines = []) => {
  const reportable = lines.filter((line) => isReportable(line))
  if (reportable.length === 0) return { coverageKey: null }
  const notMeasured = reportable.filter((line) => line.status === 'NOT_MEASURED' && (line.frequency || 0) <= 1).length
  if (notMeasured === 0) return { coverageKey: 'Complete' }
  if (notMeasured === reportable.length) return { coverageKey: 'Not measured' }
  return { coverageKey: '{{count}} not measured', coverageParams: { count: notMeasured } }
}

/** UX-9: agrupa D6b (por pieza) con la cobertura de ESTA pieza por grupo, mismo orden que la pestaña. */
export const groupFootprintInspections = (lines = []) => groupFeaturesByGroup(lines, (groupLines) => groupCoverage(groupLines))

/** RN62/D30: cobertura de la tarjeta = reportes / (reportables × N), truncada; 0 con denominador 0. */
export const coveragePercent = (summary = {}) => {
  const denominator = (summary.reportableFeatures || 0) * (summary.expectedParts || 0)
  return denominator > 0 ? Math.floor(((summary.reportedReports || 0) * 100) / denominator) : 0
}
